import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import { FormDateInput } from 'components/form-fields';
import { Modal, ModalSize } from 'components/notifications';
import { isTodayOrLater, scheduleRestartText } from 'pages/employee-reviews/review-types/shared/reviewTypeEditorHelpers';
import './ReviewTypeEditor.css';

export function ReviewTypeScheduleStartModal(
    {
        isOpen = false,
        startDate = null,
        onCancel,
        onConfirm
    }
) {

    const [selectedStartDate, setSelectedStartDate] = useState(null);

    // The review type's own start date is only offered when scheduling from it would still
    // be allowed; one already gone has to be replaced.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (isTodayOrLater(startDate)) {
            setSelectedStartDate(startDate);
        } else {
            setSelectedStartDate(null);
        }
    }, [isOpen]);

    const minimumStartDate = useMemo(() => {
        return moment().startOf('day').toDate();
    }, [isOpen]);

    function onStartDateChange(item) {
        setSelectedStartDate(item ? moment(item).format("YYYY-MM-DD") : null);
    }

    return (
        <Modal
            isOpen={isOpen}
            title="Schedule this review type"
            showCloseButton={false}
            clickOutsideClose={false}
            onClose={onCancel}
            size={ModalSize.SMALL}
        >
            <p className="review-type-schedule-start-text">{scheduleRestartText}</p>
            <FormDateInput
                id="review-type-schedule-start-date"
                headingText="New start date"
                value={selectedStartDate}
                minDate={minimumStartDate}
                onChange={onStartDateChange}
            />
            <div className="bp6-dialog-footer-actions">
                <Button
                    id="review-type-schedule-cancel"
                    text="Cancel"
                    onClick={onCancel}
                />
                <Button
                    id="review-type-schedule-confirm"
                    text="Turn on scheduling"
                    intent={Intent.PRIMARY}
                    disabled={!isTodayOrLater(selectedStartDate)}
                    onClick={() => onConfirm(selectedStartDate)}
                />
            </div>
        </Modal>
    );
}

ReviewTypeScheduleStartModal.propTypes = {
    isOpen: PropTypes.bool,
    startDate: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
};
